import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import StaffSidebar from "../components/StaffSidebar";
import "../assets/css/attendance.css";

const LEVELS = ["ND1", "ND2", "HND1", "HND2", "Postgraduate", "Diploma"];

const isProduction = process.env.NODE_ENV === "production";
const API = isProduction
  ? process.env.REACT_APP_API_BASE_URL || "https://schools.luth.gov.ng"
  : "http://localhost:3003";

function blankGrid() {
  return Array.from({ length: 12 }, () =>
    Array.from({ length: 3 }, () => false),
  );
}

function computePercent(presentSlots, totalSlots) {
  if (!totalSlots) return 0;
  return Math.round((presentSlots / totalSlots) * 10000) / 100;
}

export default function AttendancePage() {
  const { id: routeMatricNo } = useParams();

  const programme = localStorage.getItem("programme") || "";
  const staffName =
    localStorage.getItem("fullName") ||
    localStorage.getItem("username") ||
    localStorage.getItem("staffName") ||
    "Unknown Staff";

  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [grid, setGrid] = useState(blankGrid());
  const [entries, setEntries] = useState({});
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [attendanceFilter, setAttendanceFilter] = useState("all");
  const [report, setReport] = useState([]);

  useEffect(() => {
    const loadCourses = async () => {
      if (!programme || !selectedLevel) {
        setCourses([]);
        setSelectedCourse(null);
        setStudents([]);
        setSelectedStudent(null);
        return;
      }

      try {
        setLoadingCourses(true);
        setMessage("");

        const res = await axios.get(`${API}/api/staff/courses`, {
          params: {
            programme,
            level: selectedLevel,
            search: "",
          },
        });

        setCourses(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
        setMessage("Failed to load courses.");
      } finally {
        setLoadingCourses(false);
      }
    };

    loadCourses();
  }, [programme, selectedLevel]);

  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedCourse || !selectedSession) {
        setStudents([]);
        setSelectedStudent(null);
        setGrid(blankGrid());
        setEntries({});
        setReport([]);
        return;
      }

      try {
        setLoadingStudents(true);
        setMessage("");

        const res = await axios.get(
          `${API}/api/attendance/registered-students`,
          {
            params: {
              courseId: selectedCourse.CourseID,
              session: selectedSession,
              level: selectedLevel,
            },
          },
        );

        const rows = Array.isArray(res.data) ? res.data : [];
        setStudents(rows);

        const init = {};
        rows.forEach((s) => {
          init[s.MatricNumber] = {
            presentSlots: Number(s.PresentSlots || 0),
            markedSlots: Number(s.MarkedSlots || 0),
            hasAttendance: Number(s.HasAttendance || 0) === 1,
          };
        });
        setEntries(init);

        if (routeMatricNo) {
          const match = rows.find(
            (s) =>
              String(s.MatricNumber).trim() === String(routeMatricNo).trim(),
          );
          setSelectedStudent(match || rows[0] || null);
        } else {
          setSelectedStudent(rows[0] || null);
        }
      } catch (err) {
        console.error(err);
        setMessage("Failed to load registered students.");
      } finally {
        setLoadingStudents(false);
      }
    };

    loadStudents();
  }, [selectedCourse, selectedSession, selectedLevel, routeMatricNo]);

  useEffect(() => {
    const loadGrid = async () => {
      if (!selectedCourse || !selectedSession || !selectedStudent) {
        setGrid(blankGrid());
        return;
      }

      try {
        setLoadingGrid(true);
        setMessage("");

        const res = await axios.get(`${API}/api/attendance/records`, {
          params: {
            courseId: selectedCourse.CourseID,
            session: selectedSession,
            matricNo: selectedStudent.MatricNumber,
          },
        });

        if (Array.isArray(res.data?.grid)) {
          setGrid(res.data.grid);
        } else {
          setGrid(blankGrid());
        }
      } catch (err) {
        console.error(err);
        setMessage("Failed to load attendance record.");
        setGrid(blankGrid());
      } finally {
        setLoadingGrid(false);
      }
    };

    loadGrid();
  }, [selectedCourse, selectedSession, selectedStudent]);

  useEffect(() => {
    const loadReport = async () => {
      if (!selectedCourse || !selectedSession) {
        setReport([]);
        return;
      }

      try {
        const res = await axios.get(`${API}/api/attendance/report`, {
          params: {
            courseId: selectedCourse.CourseID,
            session: selectedSession,
          },
        });

        setReport(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error(err);
        setReport([]);
      }
    };

    loadReport();
  }, [selectedCourse, selectedSession]);

  const visibleStudents = useMemo(() => {
    const term = search.trim().toLowerCase();

    return students.filter((s) => {
      const name = String(s.FullName || "").toLowerCase();
      const matric = String(s.MatricNumber || "").toLowerCase();
      const hasAttendance = Number(s.HasAttendance || 0) === 1;
      const markedSlots = Number(s.MarkedSlots || 0);
      const presentSlots = Number(s.PresentSlots || 0);

      const filterOk =
        attendanceFilter === "all" ||
        (attendanceFilter === "marked" && hasAttendance) ||
        (attendanceFilter === "pending" && !hasAttendance) ||
        (attendanceFilter === "perfect" &&
          markedSlots === 36 &&
          presentSlots === 36);

      const searchOk = !term || name.includes(term) || matric.includes(term);

      return filterOk && searchOk;
    });
  }, [students, search, attendanceFilter]);

  const selectedStats = useMemo(() => {
    if (!selectedStudent) {
      return { present: 0, total: 36, percent: 0 };
    }

    const info = entries[selectedStudent.MatricNumber] || {
      presentSlots: 0,
      markedSlots: 0,
      hasAttendance: false,
    };

    const percent = computePercent(info.presentSlots || 0, 36);
    return {
      present: info.presentSlots || 0,
      total: 36,
      percent,
    };
  }, [entries, selectedStudent]);

  const toggle = (weekIndex, classIndex) => {
    const copy = grid.map((row) => [...row]);
    copy[weekIndex][classIndex] = !copy[weekIndex][classIndex];
    setGrid(copy);
  };

  const save = async () => {
    if (!selectedCourse || !selectedSession || !selectedStudent) {
      setMessage("Select a course, session, and student first.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      await axios.post(
        `${API}/api/attendance/save`,
        {
          matricNo: selectedStudent.MatricNumber,
          courseId: selectedCourse.CourseID,
          session: selectedSession,
          grid,
          enteredBy: staffName,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-name": staffName,
          },
        },
      );

      setMessage("Attendance saved successfully.");

      const refresh = await axios.get(
        `${API}/api/attendance/registered-students`,
        {
          params: {
            courseId: selectedCourse.CourseID,
            session: selectedSession,
            level: selectedLevel,
          },
        },
      );

      const rows = Array.isArray(refresh.data) ? refresh.data : [];
      setStudents(rows);

      const init = {};
      rows.forEach((s) => {
        init[s.MatricNumber] = {
          presentSlots: Number(s.PresentSlots || 0),
          markedSlots: Number(s.MarkedSlots || 0),
          hasAttendance: Number(s.HasAttendance || 0) === 1,
        };
      });
      setEntries(init);

      const rec = await axios.get(`${API}/api/attendance/records`, {
        params: {
          courseId: selectedCourse.CourseID,
          session: selectedSession,
          matricNo: selectedStudent.MatricNumber,
        },
      });
      setGrid(Array.isArray(rec.data?.grid) ? rec.data.grid : blankGrid());
    } catch (err) {
      console.error(err);
      setMessage(
        err.response?.data?.msg || err.response?.data?.detail || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const attendancePercent = selectedStudent
    ? computePercent(
        Number(entries[selectedStudent.MatricNumber]?.presentSlots || 0),
        36,
      )
    : 0;

  const courseInfo = selectedCourse
    ? `${selectedCourse.CourseCode} • ${selectedCourse.CourseTitle}`
    : "Select a course";

  return (
    <div className="staff-dashboard" style={{ marginTop: "-130px" }}>
      <StaffSidebar />

      <main className="attendance-page">
        <div className="attendance-header">
          <div>
            <h1>Attendance Tracking</h1>
            <p>
              {programme || "No programme selected"} · {courseInfo}
            </p>
          </div>
          <div className="attendance-pill">
            {selectedSession || "Select session"}
          </div>
        </div>

        <section className="attendance-filters">
          <div className="filter-card">
            <label>Level</label>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
            >
              <option value="">-- Select Level --</option>
              {LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-card">
            <label>Course</label>
            <select
              value={selectedCourse?.CourseID || ""}
              onChange={(e) => {
                const cid = Number(e.target.value);
                const found = courses.find((c) => Number(c.CourseID) === cid);
                setSelectedCourse(found || null);
                setSelectedStudent(null);
                setSearch("");
              }}
              disabled={!selectedLevel || loadingCourses}
            >
              <option value="">
                {loadingCourses ? "Loading courses..." : "-- Select Course --"}
              </option>
              {courses.map((c) => (
                <option key={c.CourseID} value={c.CourseID}>
                  {c.CourseCode} - {c.CourseTitle}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-card">
            <label>Session</label>
            <input
              type="text"
              placeholder="e.g. 2025/2026"
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.target.value)}
            />
          </div>

          <div className="filter-card">
            <label>Search student</label>
            <input
              type="text"
              placeholder="Name or matric number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!selectedCourse}
            />
          </div>

          <div className="filter-card">
            <label>Attendance Filter</label>
            <select
              value={attendanceFilter}
              onChange={(e) => setAttendanceFilter(e.target.value)}
            >
              <option value="all">All Students</option>
              <option value="marked">Attendance Started</option>
              <option value="pending">Not Started</option>
              <option value="perfect">Perfect Attendance</option>
            </select>
          </div>
        </section>

        <section className="attendance-layout">
          <aside className="attendance-list-card">
            <div className="list-head">
              <h3>Registered Students</h3>
              <span>{visibleStudents.length}</span>
            </div>

            <div className="attendance-student-list">
              {visibleStudents.map((s) => {
                const isActive =
                  selectedStudent &&
                  String(selectedStudent.MatricNumber) ===
                    String(s.MatricNumber);
                const presentSlots = Number(s.PresentSlots || 0);
                const percent = computePercent(presentSlots, 36);

                return (
                  <button
                    key={s.MatricNumber}
                    type="button"
                    className={`student-item ${isActive ? "active" : ""}`}
                    onClick={() => setSelectedStudent(s)}
                  >
                    <div>
                      <strong>{s.FullName}</strong>
                      <div>{s.MatricNumber}</div>
                    </div>
                    <div className="student-meta">
                      <span>{percent}%</span>
                      <small>{s.HasAttendance ? "Recorded" : "New"}</small>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="attendance-grid-card">
            {!selectedCourse || !selectedSession ? (
              <div className="empty-state">
                Select a course and session to begin.
              </div>
            ) : !selectedStudent ? (
              <div className="empty-state">
                Select a student to view and save attendance.
              </div>
            ) : loadingGrid ? (
              <div className="empty-state">Loading attendance...</div>
            ) : (
              <>
                <div className="grid-top">
                  <div>
                    <h2>{selectedStudent.FullName}</h2>
                    <p>
                      {selectedStudent.MatricNumber} ·{" "}
                      {selectedStudent.StudentLevel}
                    </p>
                  </div>

                  <div className="grid-stat">
                    <span>Present</span>
                    <strong>
                      {selectedStats.present} / {selectedStats.total}
                    </strong>
                    <small>{attendancePercent}%</small>
                  </div>
                </div>

                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Week</th>
                      <th>Class 1</th>
                      <th>Class 2</th>
                      <th>Class 3</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grid.map((weekRow, w) => (
                      <tr key={w}>
                        <td>Week {w + 1}</td>
                        {weekRow.map((present, c) => (
                          <td key={c}>
                            <input
                              type="checkbox"
                              checked={present}
                              onChange={() => toggle(w, c)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {message && <div className="message-box">{message}</div>}

                <div className="attendance-actions">
                  <button
                    type="button"
                    onClick={save}
                    className="btn-save"
                    disabled={saving || !selectedStudent}
                  >
                    {saving ? "Saving..." : "Save Attendance"}
                  </button>
                </div>

                <div className="report-summary">
                  <h3>Course Attendance Summary</h3>
                  <div className="report-grid">
                    {report.map((r) => (
                      <div key={r.MatricNumber} className="report-card">
                        <strong>{r.MatricNumber}</strong>
                        <span>{r.PresentSlots} present</span>
                        <span>{r.AbsentSlots} absent</span>
                        <span>{r.AttendancePercent}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>
        </section>
      </main>
    </div>
  );
}
